import { Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';

import {
  type CalendarEntry,
  type CalendarKind,
  type CalendarTerm,
  covers,
  DATE,
  isWeekend,
  monthGrid,
  monthName,
  WEEKDAYS,
} from './types';

interface Props {
  // 1 September of the season shown.
  from: Dayjs;
  entries: CalendarEntry[];
  kinds: Map<number, CalendarKind>;
  terms: CalendarTerm[];
  onPickMonth: (month: Dayjs) => void;
}

// The season's 12 months (September – August) at a glance: Holidays fill
// their days in their kind's colour, other entries show a dot, days outside
// every term are greyed. A day opens its month.
export default function YearView({ from, entries, kinds, terms, onPickMonth }: Props) {
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => from.add(i, 'month')), [from]);
  const today = dayjs().format(DATE);

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {months.map((month) => (
        <div
          key={month.format('YYYY-MM')}
          className="border border-gray-100 p-3 transition hover:border-gray-200 hover:shadow-sm"
        >
          <button
            type="button"
            onClick={() => onPickMonth(month)}
            className="mb-2 cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-gray-700 hover:text-blue-600"
          >
            {monthName(month)} <span className="font-normal text-gray-400">{month.year()}</span>
          </button>
          <div className="grid grid-cols-7 text-center">
            {WEEKDAYS.map((d) => (
              <div key={d} className="pb-1 text-[10px] font-medium text-gray-400">
                {d}
              </div>
            ))}
            {monthGrid(month)
              .flat()
              .map((day) => {
                const date = day.format(DATE);
                if (day.month() !== month.month()) return <div key={date} className="h-7" />;
                const dayEntries = entries.filter((e) => covers(e, date));
                const holiday = dayEntries.find((e) => kinds.get(e.kind_id)?.no_lessons);
                const other = dayEntries.find((e) => !kinds.get(e.kind_id)?.no_lessons);
                const inTerm = terms.some((t) => covers(t, date));
                const cell = (
                  <button
                    key={date}
                    type="button"
                    onClick={() => onPickMonth(month)}
                    className={`relative mx-auto flex size-7 cursor-pointer items-center justify-center border-0 text-[11px] transition hover:ring-2 hover:ring-blue-200 ${
                      date === today
                        ? 'bg-blue-600 font-semibold text-white'
                        : holiday
                          ? 'font-semibold text-white'
                          : !inTerm || isWeekend(day)
                            ? 'bg-transparent text-gray-400'
                            : 'bg-transparent text-gray-700'
                    }`}
                    style={
                      holiday && date !== today
                        ? { backgroundColor: kinds.get(holiday.kind_id)?.color }
                        : undefined
                    }
                  >
                    {day.date()}
                    {other && (
                      <span
                        className="absolute bottom-0.5 size-1"
                        style={{
                          backgroundColor: holiday ? '#fff' : kinds.get(other.kind_id)?.color,
                        }}
                      />
                    )}
                  </button>
                );
                return dayEntries.length ? (
                  <Tooltip
                    key={date}
                    title={
                      <div className="text-xs">
                        <div className="mb-1 font-semibold">{day.format('YYYY.MM.DD')}</div>
                        {dayEntries.map((e) => (
                          <div key={e.id} className="flex items-center gap-1.5">
                            <span
                              className="size-2 shrink-0"
                              style={{ backgroundColor: kinds.get(e.kind_id)?.color }}
                            />
                            {e.start_time && <span className="opacity-70">{e.start_time}</span>}
                            {e.title}
                          </div>
                        ))}
                      </div>
                    }
                  >
                    {cell}
                  </Tooltip>
                ) : (
                  <div key={date}>{cell}</div>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}
